import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { SceneBase } from './SceneBase';
import { GameController } from '../atoms/gaming/GameController';
import { GamingPixels } from '../atoms/gaming/GamingPixels';
import { glowPulse, glowPulseReduced } from '../../../../../theme/animations';

/**
 * GamingScene - Card background for gaming venue type.
 *
 * Features a dark purple gradient, pixel grid pattern, neon glow edge,
 * and decorative gaming atoms. Hover intensifies the glow effect.
 */
export const GamingScene = React.memo(function GamingScene() {
  const theme = useTheme();
  const gamingColor = theme.palette.venue.gaming;

  return (
    <SceneBase>
      {({ isMobile, shouldReduceMotion }) => (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            // Dark purple gradient background
            background: `linear-gradient(135deg, ${gamingColor}26 0%, ${gamingColor}0D 100%)`,
            // Pixel grid CSS pattern
            backgroundImage: `
              linear-gradient(135deg, ${gamingColor}26 0%, ${gamingColor}0D 100%),
              repeating-linear-gradient(
                0deg,
                transparent,
                transparent 19px,
                ${gamingColor}08 19px,
                ${gamingColor}08 20px
              ),
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 19px,
                ${gamingColor}08 19px,
                ${gamingColor}08 20px
              )
            `,
            // Neon glow edge
            boxShadow: `inset 0 0 30px ${gamingColor}1A`,
            // Hover intensification - CSS only, hover-capable devices only
            transition: 'box-shadow 0.3s ease, background 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': {
                boxShadow: `inset 0 0 50px ${gamingColor}33`,
                background: `linear-gradient(135deg, ${gamingColor}33 0%, ${gamingColor}15 100%)`,
              },
            },
          }}
        >
          {/* GameController atom - bottom right */}
          <motion.div
            variants={shouldReduceMotion ? glowPulseReduced : glowPulse}
            initial="hidden"
            animate="visible"
            style={{
              position: 'absolute',
              bottom: isMobile ? '8%' : '10%',
              right: isMobile ? '6%' : '8%',
              opacity: 0.3,
            }}
          >
            <GameController size={isMobile ? 40 : 56} />
          </motion.div>

          {/* GamingPixels atom - scattered mid-left */}
          <motion.div
            variants={shouldReduceMotion ? glowPulseReduced : glowPulse}
            initial="hidden"
            animate="visible"
            style={{
              position: 'absolute',
              top: isMobile ? '10%' : '15%',
              left: isMobile ? '5%' : '8%',
              opacity: 0.2,
            }}
          >
            <GamingPixels size={isMobile ? 32 : 44} />
          </motion.div>

          {/* Additional pixels - top right (desktop only) */}
          {!isMobile && (
            <motion.div
              variants={shouldReduceMotion ? glowPulseReduced : glowPulse}
              initial="hidden"
              animate="visible"
              style={{
                position: 'absolute',
                top: '8%',
                right: '12%',
                opacity: 0.15,
              }}
            >
              <GamingPixels size={28} />
            </motion.div>
          )}
        </Box>
      )}
    </SceneBase>
  );
});
